import { StoreConstructor } from "./StoreConstructor";
import { statusFetching } from "services/authClient/constants/types";
import { reaction, action, computed, observable, runInAction, makeObservable } from "mobx";
import { NormalizedError } from "services/api/errorHandler";
import _ from "lodash";

interface ServerResponse<T> {
  content: T[];
  size: number;
  totalPages: number;
  totalElements: number;
}

export interface ListData<T> {
  list: T[];
  size: number;
  totalPages: number;
  totalElements: number;
}

interface IListStoreOptions {
  pollingInterval?: number;
  paginationData?: IPagination;
  sorter?: string[];
  sorters?: ISorters;
  filters?: IFilters;
  isLocal?: boolean;
  searchFields?: string[];
  defaultSorters?: ISorters;
}

export type TSorter = undefined | "asc" | "desc";

interface ISorters {
  [name: string]: TSorter;
}

interface IFilters {
  [name: string]: any;
}

interface IPagination {
  currentPage?: number;
  totalPages?: number;
  totalElements?: number;
  pageSize?: number;
}

export interface OnChangeDataFlowOptions {
  withDebounce?: boolean;
  patchMode?: "smart" | "simple";
  initialize?: boolean;
}

export interface IDataFlowProps {
  paginationData?: IPagination;
  sorter?: string | string[];
  sorters?: ISorters;
  filters?: IFilters;
}

export interface IQueryParams extends IFilters {
  sort: string | string[] | undefined;
  size: number | undefined;
  page: number;
}

//type EndpointFn = <T>(params: any) => Promise<{ content: T[] }>;

export class ListStoreConstructor<T> extends StoreConstructor {
  public total: number | null = null;
  public allData: T[] = [];
  public fetchStatus: statusFetching = "init";
  public paginationData: IPagination = {};
  public sorters: ISorters = {};
  public defaultSorters: ISorters = {};
  public sorter?: string | string[];
  public filters: IFilters = {};
  public defaultFilters: IFilters = {};
  public fetchError?: NormalizedError;
  public endpointFn: (params: any) => Promise<{ content: T[]; [name: string]: any }>;
  public debouncedFetch: any;
  public pollingInterval: number;
  tId: any;

  reactionId: any;

  isLocal = false;
  searchFields: string[] = [];

  public get isPending() {
    return this.fetchStatus === "fetching";
  }

  constructor(stores: any, endpoint: (params: any) => Promise<{ content: T[] }>, options: IListStoreOptions) {
    super(stores);
    this.endpointFn = endpoint;

    const { pollingInterval = 0 } = options;
    this.pollingInterval = pollingInterval;

    this.sorters = options.sorters || {};
    this.defaultSorters = options.defaultSorters || {};
    this.sorter = "none";
    this.filters = options.filters || {};
    this.defaultFilters = options.filters || {};

    this.isLocal = !!options.isLocal;
    this.searchFields = options.searchFields || [];

    this.paginationData = {
      currentPage: 1,
      totalPages: 1,
      pageSize: 10,
      ...(options ? options.paginationData : {}),
    };

    this.onChangeDataFlow = this.onChangeDataFlow.bind(this);

    makeObservable(this, {
      total: observable,
      allData: observable.ref,
      fetchStatus: observable,
      paginationData: observable,
      sorters: observable,
      defaultSorters: observable,
      sorter: observable,
      filters: observable,
      fetchError: observable,
      isLocal: observable,
      searchFields: observable,
      isPending: computed,
      queryParams: computed,
      filteredData: computed,
      sortedData: computed,
      data: computed,
      patchState: action,
      removeFilters: action,
      clearData: action,
      onChangeDataFlow: action,
      fetch: action,
      clear: action,
      updatePagination: action,
    });

    if (this.isLocal) {
      if (this.reactionId) this.reactionId();

      this.reactionId = reaction(
        () => this.filteredData,
        () => {
          runInAction(() => {
            this.paginationData.totalPages = Math.ceil(this.filteredData.length / Number(this.paginationData.pageSize));
          });
        }
      );
    }
  }

  init(options?: IListStoreOptions) {
    clearInterval(this.tId);
    this.tId = null;

    if (!_.isEmpty(options)) {
      this.onChangeDataFlow(options, { patchMode: "simple" });
    } else {
      // this.fetch(); //Commented to prevent double fetch TableC component in componentWillMount method
    }

    if (this.pollingInterval > 0 && !this.isLocal) {
      this.tId = setInterval(() => {
        !this.isPending && this.fetch({ isSilent: true });
      }, this.pollingInterval);
    }
  }

  public get dataFlow() {
    return {
      paginationData: this.paginationData,
      sorters: this.sorters,
      sorter: this.sorter,
      filters: this.filters,
    };
  }

  onChangeDataFlow(props: IDataFlowProps, options: OnChangeDataFlowOptions = {}) {
    const { withDebounce = false, patchMode = "smart", initialize = false } = options;

    if (patchMode === "smart") {
      this.smartStatePatch(props);
    } else {
      this.patchState(props);
    }

    if (!this.isLocal) {
      this.fetch({
        withDebounce,
        initialize,
      });
    }
  }

  /**
   * Patches state with extra logic
   */
  private smartStatePatch(state: IDataFlowProps) {
    const { paginationData: newPaginationState = {}, sorter, sorters, filters } = state;

    const paginationData = { ...newPaginationState };

    // recalc total pages and reset current page on change pageSize
    if (paginationData.pageSize !== this.paginationData.pageSize) {
      const totalElements = this.paginationData.totalElements;
      const pageSize = paginationData.pageSize;

      const totalPages = Math.max(Math.ceil(Number(totalElements) / Number(pageSize)), 1);

      paginationData.currentPage = 1;
      paginationData.totalPages = totalPages;
    }

    // reset current page on new filters
    // if (!_.isEmpty(filters)) {
    //   paginationData.currentPage = 1;
    // }

    this.patchState({
      paginationData,
      sorters,
      filters,
      sorter,
    });
  }

  // @action.bound
  patchState(state: IDataFlowProps) {
    const { paginationData = {}, sorter, sorters = {}, filters = {} } = state;

    this.paginationData = {
      ...this.paginationData,
      ...paginationData,
    };

    this.filters = {
      ...this.filters,
      ...filters,
    };

    this.sorters = Object.keys(sorters).length
      ? {
          ...sorters,
        }
      : this.defaultSorters;

    this.sorter = this.calcSorter(sorter);
  }

  private calcSorter(sorter: string | string[] | undefined) {
    if (_.isArray(sorter)) return sorter;
    if (sorter === "none") return undefined;
    return sorter || this.sorter;
  }

  removeFilters() {
    const clearedFilters = Object.entries(this.filters).reduce((acc: any, [name]) => {
      acc[name] = undefined;
      return acc;
    }, {});

    this.onChangeDataFlow({
      filters: { ...clearedFilters, ...this.defaultFilters },
      sorters: this.defaultSorters,
    });
  }

  updatePagination(res: ServerResponse<T>) {
    const { totalPages, size, totalElements } = res;
    const p = this.paginationData;

    if (p.totalPages !== totalPages || p.totalElements !== totalElements || p.pageSize !== size) {
      this.paginationData = { ...p, totalPages, totalElements, pageSize: size };
    }
  }

  get queryParams(): IQueryParams {
    const { currentPage = 1, pageSize = 10 } = this.paginationData;
    let sort: string[] = [];

    const tempSorters = Object.keys(this.sorters).length ? { ...this.sorters } : { ...this.defaultSorters };
    const statusDateValue = tempSorters.statusDate;
    delete tempSorters.statusDate;

    Object.entries(tempSorters).forEach(([key, value]) => {
      sort.push(`${key},${value}`);
    });

    if (statusDateValue !== undefined) {
      sort.push(`statusDate,${statusDateValue}`);
    }

    return {
      page: currentPage - 1,
      size: pageSize,
      sort: sort.length > 0 ? sort : undefined,
      ...this.filters,
    };
  }

  get filteredData() {
    if (!this.isLocal) return this.allData;

    let data = this.allData;

    if (this.filters.search && this.searchFields.length) {
      data = data.filter((row: any) => {
        return this.searchFields.some((key) => {
          if (typeof row[key] !== "string") {
            return false;
          }

          return row[key].toLowerCase().includes(this.filters.search.toLowerCase());
        });
      });
    }

    return data.filter((rowData: any) =>
      Object.entries(this.filters)
        .filter(([key, value]) => !!value && key !== "search")
        .every(([key, value]) => {
          return value instanceof Array ? value.includes(rowData[key]) : rowData[key].indexOf(value) > -1;
        })
    );
  }

  get sortedData() {
    if (!this.isLocal) return this.filteredData;
    const data = this.filteredData.map((a) => a);

    if (!this.sorter || this.sorter === "none" || this.sorter instanceof Array) {
      return data;
    }

    const [index, direction] = this.sorter.split(",");
    const dir = direction === "asc" ? 1 : -1;
    return data.sort((a: any, b: any) => {
      return a[index] < b[index] ? dir : -dir;
    });
  }

  get data() {
    if (this.isLocal) {
      const { pageSize, currentPage } = this.paginationData;

      return this.sortedData.splice(Number(pageSize) * (Number(currentPage) - 1), pageSize);
    } else {
      return this.allData;
    }
  }

  fetch = (
    options: {
      isSilent?: boolean;
      withDebounce?: boolean;
      initialize?: boolean;
    } = {}
  ) => {
    const { isSilent = false, withDebounce = false, initialize = false } = options;

    if (withDebounce) {
      if (this.debouncedFetch) {
        return this.debouncedFetch();
      } else {
        //this.fetch();
        this.debouncedFetch = _.debounce(this.fetch, 300);
        return;
      }
    }

    if (!isSilent) {
      this.fetchStatus = "fetching";
    }

    const params = this.isLocal ? null : this.prepareQueryParams({ ...this.queryParams });

    return this.endpointFn(params)
      .then((res: any) => {
        runInAction(() => {
          this.fetchStatus = "success";

          if (initialize) {
            this.total = res.totalElements;
          }

          this.allData = res.content;

          if (!this.isLocal) {
            this.updatePagination(res);
          }
        });

        return res;
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.fetchStatus = "error";
          this.fetchError = error;
        });
      });
  };

  clear() {
    this.allData = [];
    clearInterval(this.tId);
    this.tId = null;
    this.fetchStatus = "init";
    this.filters = {};
  }

  // @action.bound
  clearData() {
    this.allData = [];
  }

  // @action
  // удаляем пустые значения из url, например ?status=
  prepareQueryParams(params: any) {
    for (let k in params) {
      if (["page", "size", "sort"].includes(k)) continue;

      const value = params[k];

      if (value === null || value === undefined) {
        delete params[k];
        continue;
      }

      if (Array.isArray(value)) {
        if (!value.length) delete params[k];
        continue;
      }

      if (typeof value === "object" && Object.keys(value).length === 0) {
        delete params[k];
        continue;
      }

      if (value === "") {
        delete params[k];
      }
    }

    return params;
  }
}
